import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './Login.css';
import loginlogo from '../../src/safreeimg/safreelogo.png';
import { getFcmToken } from './getFcmToken';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('');
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Function to call login API
  const proceedLogin = async (deviceToken) => {
    try {
      const response = await axios.post(
        'https://api.safree.org/api-v2022/admin/auth/v1/login',
        {
          email,
          password,
          role,
          deviceToken: deviceToken || '',
        }
      );

      if (response.data && response.data.code === 200) {
        const { authorizationToken } = response.data;
        localStorage.setItem('token', authorizationToken);
        localStorage.setItem('role', role);

        const roleMap = {
          SuperAdmin: 'admin1',
          Organization: 'admin2',
          Guardian: 'admin3',
        };

        const adminPath = roleMap[role];
        if (adminPath) {
          navigate(`/${adminPath}`);
        } else {
          alert('Invalid role selection.');
        }
      } else {
        alert('Login failed. Please check credentials.');
      }
    } catch (error) {
      console.error('Login error:', error);
      alert('Please validate your information with specific User type');
    }
  };

  // Function to call update FCM API after login
  const updateFcmAfterLogin = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.log("No token in storage, user not logged in yet");
        return;
      }

      const fcmToken = await getFcmToken();
      if (!fcmToken) {
        console.log("No FCM token available to update");
        return;
      }

      console.log("Updating FCM token after permission granted:", fcmToken);

      await axios.post(
        'https://api.safree.org/api-v2022/admin/org/guardian/v1/update-fcm',
        { fcmToken },
        {
          headers: {
            Authorization: `${token}`,
          },
        }
      );

      console.log("FCM token updated successfully.");
    } catch (error) {
      console.error("Error updating FCM token:", error);
    }
  };

  // Detect if notification permission changes (e.g. user allows notifications from browser settings after login)
  useEffect(() => {
    let lastPermission = Notification.permission;
    let interval = null;

    if (lastPermission !== 'granted') {
      interval = setInterval(() => {
        if (Notification.permission !== lastPermission) {
          lastPermission = Notification.permission;

          if (lastPermission === 'granted') {
            console.log("Notification permission granted - updating FCM token");
            updateFcmAfterLogin();
            clearInterval(interval); // stop checking after granted
          }
        }
      }, 2000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, []);

  const handleLogin = async () => {
    if (!email || !password || !role) {
      alert('Please fill all fields and select a role.');
      return;
    }

    setIsSubmitting(true);

    try {
      if (Notification.permission === 'granted') {
        const deviceToken = await getFcmToken();
        await proceedLogin(deviceToken);
      } else {
        const userChoice = window.confirm(
          "Notifications are not enabled.\n\nDo you want to enable them?"
        );

        if (userChoice) {
          const permission = await Notification.requestPermission();
          if (permission === 'granted') {
            alert('Notifications enabled. Please click Login again to proceed.');
            // Don't proceed now, user must click login again
          } else {
            // User denied or dismissed notification permission, proceed without token
            await proceedLogin('');
          }
        } else {
          // User chose not to enable notifications, proceed without token
          await proceedLogin('');
        }
      }
    } catch (error) {
      console.error('Login error:', error);
      alert('Login failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className='container-fluid login'>
      <div className="row justify-content-center align-items-center">
        <div className="login-container">
          <img src={loginlogo} alt="Logo" />
          <form className="login-form">
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input
                type="email"
                id="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input
                type="password"
                id="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label htmlFor="role">Select Role</label>
              <select
                id="role"
                value={role}
                onChange={(e) => setRole(e.target.value)}
              >
                <option value="">-- Select Role --</option>
                <option value="SuperAdmin">Super Admin</option>
                <option value="Organization">Organization</option>
                <option value="Guardian">Guardian</option>
              </select>
            </div>
            <button type="button" onClick={handleLogin} disabled={isSubmitting}>
              {isSubmitting ? (
                <div className="spinner-border text-light" style={{ width: '20px', height: '20px' }} role="status" />
              ) : (
                'Login'
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;
